// File: src/contexts/ThemeContext.tsx

import React, { createContext, useContext, useState, useEffect } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

export const ThemeProvider: React.FC<{
    children: React.ReactNode;
    initialTheme?: "light" | "dark";
}> = ({ children, initialTheme }) => {
    const [theme, setTheme] = useState<Theme>(() => {
        // Always check localStorage first to preserve user's theme preference
        // This ensures theme persists across widget re-initialization (e.g., SPA navigation)
        const savedTheme = localStorage.getItem("theme") as Theme;
        if (savedTheme) {
            return savedTheme;
        }
        // If no saved theme, use initialTheme from props if provided
        if (initialTheme) {
            return initialTheme;
        }
        // Default to light
        return "light";
    });

    useEffect(() => {
        // Save to localStorage
        localStorage.setItem("theme", theme);

        // Check if we're running as a widget or main app
        const isWidget = document.getElementById("chat-widget-container") !== null;

        if (isWidget) {
            // Widget mode: only update widget container
            const widgetContainer = document.getElementById("chat-widget-container");
            if (widgetContainer) {
                widgetContainer.setAttribute("data-theme", theme);
            }
            // Also mirror to document root for global-layered components (Fluent UI Modal)
            document.documentElement.setAttribute("data-widget-theme", theme);
        } else {
            // Main app mode: update document root
            document.documentElement.setAttribute("data-theme", theme);
            document.documentElement.setAttribute("data-widget-theme", theme);
        }
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === "dark" ? "light" : "dark"));
    };

    return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
};
